import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import CarDetailsForm from "@/components/CarDetailsForm";
import AudioUploader from "@/components/AudioUploader";
import ProcessingSteps from "@/components/ProcessingSteps";
import ResultsDashboard from "@/components/ResultsDashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Upload, RotateCcw, Globe, Plug, CheckCircle, AlertCircle, Loader2, Info } from "lucide-react";

const COUNTRIES = [
  { code: "IN", name: "India", currency: "INR" },
  { code: "TH", name: "Thailand", currency: "THB" },
  { code: "MY", name: "Malaysia", currency: "MYR" },
  { code: "PH", name: "Philippines", currency: "PHP" },
];

const STEP_MAP = {
  queued: 1,
  processing: 1,
  loading_audio: 1,
  validating: 2,
  noise_cancelling: 3,
  analyzing_sound: 4,
  ai_analyzing: 5,
  completed: 6,
  error: 0,
};

export default function AnalysisPage() {
  const location = useLocation();
  const [carData, setCarData] = useState(null);
  const [country, setCountry] = useState("IN");
  const [carDetails, setCarDetails] = useState({
    make: "", model: "", year: "", transmission: "", fuel_type: "", km_driven: "", has_turbo: null
  });
  const [audioFile, setAudioFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentAnalysisId, setCurrentAnalysisId] = useState(null); // Track analysis ID for debugging
  const pollRef = useRef(null);
  
  // OBD Integration state
  const [obdEnabled, setObdEnabled] = useState(false);
  const [obdScanId, setObdScanId] = useState("");
  const [obdVerifyStatus, setObdVerifyStatus] = useState(null); // null, 'loading', 'valid', 'invalid'
  const [obdData, setObdData] = useState(null);

  // Load car data based on selected country
  const loadCarData = useCallback((countryCode) => {
    api.getCarData(countryCode)
      .then(r => setCarData(r.data))
      .catch(() => toast.error("Failed to load car data"));
  }, []);

  useEffect(() => {
    loadCarData(country);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Handle country change - reload car data and reset selections
  const handleCountryChange = (newCountry) => {
    setCountry(newCountry);
    setCarDetails(prev => ({ ...prev, make: "", model: "" }));
    loadCarData(newCountry);
  };

  // Auto-fill from Vehicle ID (Vision AI)
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill || !carData) return;

    // Find best matching make (case-insensitive)
    const matchMake = carData.makes?.find(m => m.toLowerCase() === prefill.make.toLowerCase()) || "";

    // Find best matching model
    let matchModel = "";
    if (matchMake) {
      const models = carData.models_by_make?.[matchMake] || [];
      matchModel = models.find(m => m.toLowerCase() === prefill.model.toLowerCase())
        || models.find(m => m.toLowerCase().includes(prefill.model.toLowerCase()))
        || models.find(m => prefill.model.toLowerCase().includes(m.toLowerCase()))
        || "";
    }

    // Find best matching fuel type
    const fuelMap = { "petrol": "Petrol", "diesel": "Diesel", "hybrid": "Hybrid", "electric": "Electric", "cng": "CNG", "lpg": "LPG" };
    const matchFuel = carData.fuel_types?.find(f => f.toLowerCase() === (prefill.fuel_type || "").toLowerCase())
      || fuelMap[(prefill.fuel_type || "").toLowerCase()] || "";

    // Year — pick first year from range, validate against available years
    let matchYear = "";
    if (prefill.year && carData.years?.includes(prefill.year)) {
      matchYear = prefill.year;
    }

    const newDetails = { ...carDetails };
    if (matchMake) newDetails.make = matchMake;
    if (matchModel) newDetails.model = matchModel;
    if (matchYear) newDetails.year = matchYear;
    if (matchFuel) newDetails.fuel_type = matchFuel;

    setCarDetails(newDetails);

    // Clear the navigation state so refresh doesn't re-apply
    window.history.replaceState({}, document.title);

    const filled = [matchMake && "Make", matchModel && "Model", matchYear && "Year", matchFuel && "Fuel"].filter(Boolean);
    if (filled.length > 0) {
      toast.success(`Auto-filled: ${filled.join(", ")}`, { description: "From Vehicle ID result. Please verify and complete remaining fields." });
    }
  }, [carData, location.state]);

  // OBD Scan ID verification
  const verifyObdScan = useCallback(async (scanId) => {
    if (!scanId || scanId.length < 4) {
      setObdVerifyStatus(null);
      setObdData(null);
      return;
    }
    
    setObdVerifyStatus('loading');
    try {
      const res = await api.getObdScan(scanId);
      setObdData(res.data);
      setObdVerifyStatus('valid');
      toast.success(`OBD Scan found: ${res.data.storedDTCs?.length || 0} DTCs, ${res.data.liveData?.length || 0} live readings`);
    } catch (err) {
      setObdVerifyStatus('invalid');
      setObdData(null);
      if (err.response?.status === 404) {
        toast.error("OBD Scan ID not found");
      } else {
        toast.error("Failed to verify OBD Scan");
      }
    }
  }, []);

  // Debounced OBD verification
  useEffect(() => {
    if (!obdEnabled || !obdScanId) {
      setObdVerifyStatus(null);
      setObdData(null);
      return;
    }
    
    const timer = setTimeout(() => {
      verifyObdScan(obdScanId);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [obdScanId, obdEnabled, verifyObdScan]);

  const isFormValid = carDetails.make && carDetails.model && carDetails.year &&
    carDetails.transmission && carDetails.fuel_type && carDetails.km_driven && 
    (carDetails.has_turbo === true || carDetails.has_turbo === false) && // Turbo is mandatory
    audioFile &&
    (!obdEnabled || (obdEnabled && obdVerifyStatus === 'valid'));

  const pollForResults = useCallback((analysisId) => {
    let attempts = 0;
    const maxAttempts = 120; // 120 * 2s = 4 minutes max

    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(pollRef.current);
        setIsAnalyzing(false);
        setCurrentStep(0);
        toast.error("Analysis timed out. Please try again.");
        return;
      }

      try {
        const res = await api.getSoundStatus(analysisId);
        const data = res.data;
        const step = STEP_MAP[data.status] ?? 1;
        setCurrentStep(step);

        if (data.status === "completed") {
          clearInterval(pollRef.current);
          setAnalysisResult(data);
          setIsAnalyzing(false);
          toast.success("Analysis complete!");
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          setIsAnalyzing(false);
          setCurrentStep(0);
          toast.error(data.error_message || "Analysis failed");
        }
      } catch {
        // Ignore transient poll errors
      }
    }, 2000);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!isFormValid) {
      if (obdEnabled && obdVerifyStatus !== 'valid') {
        toast.error("Please enter a valid OBD Scan ID or disable OBD analysis");
      } else {
        toast.error("Please fill all fields and upload an audio file");
      }
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult(null);
    setCurrentStep(1);

    const formData = new FormData();
    formData.append("audio_file", audioFile);
    formData.append("country", country);
    Object.entries(carDetails).forEach(([k, v]) => formData.append(k, v));
    
    // Add OBD scan ID if enabled and valid
    if (obdEnabled && obdVerifyStatus === 'valid' && obdScanId) {
      formData.append("obd_scan_id", obdScanId);
    }

    try {
      const response = await api.submitSound(formData);

      const { id, status, has_obd, from_cache } = response.data;
      
      // Check if result is from cache (immediate completion)
      if (status === "completed" && from_cache) {
        setCurrentAnalysisId(id);
        toast.success("Instant result from cache!");
        // Fetch full results immediately
        try {
          const fullResult = await api.getSoundStatus(id);
          setAnalysisResult(fullResult.data);
          setIsAnalyzing(false);
          setCurrentStep(6);
        } catch {
          // Fallback: poll for results
          pollForResults(id);
        }
      } else if ((status === "processing" || status === "queued") && id) {
        setCurrentAnalysisId(id); // Store analysis ID for display
        const obdMsg = has_obd ? " (with OBD data)" : "";
        toast.info(`Analysis ID: ${id.substring(0, 8)}... Processing${obdMsg}`);
        pollForResults(id);
      } else if (response.data.error) {
        toast.error(response.data.error);
        setIsAnalyzing(false);
        setCurrentStep(0);
      }
    } catch (err) {
      setCurrentStep(0);
      setIsAnalyzing(false);
      const msg = err.response?.data?.detail || err.message || "Upload failed. Please try again.";
      toast.error(msg);
    }
  }, [isFormValid, audioFile, carDetails, country, pollForResults, obdEnabled, obdVerifyStatus, obdScanId]);

  const handleReset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setAnalysisResult(null);
    setCurrentStep(0);
    setAudioFile(null);
    setIsAnalyzing(false);
    setObdEnabled(false);
    setObdScanId("");
    setObdVerifyStatus(null);
    setObdData(null);
    setCurrentAnalysisId(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow'] text-slate-900" data-testid="page-title">
            Engine Sound Analysis
          </h1>
          <p className="text-base text-slate-500 mt-1">Upload engine audio for AI-powered diagnostic analysis</p>
        </div>
        {(analysisResult || isAnalyzing) && (
          <Button
            onClick={handleReset}
            variant="outline"
            className="uppercase tracking-wider font-bold text-sm rounded-sm px-5 py-2.5"
            data-testid="new-analysis-btn"
          >
            <RotateCcw size={16} className="mr-2" /> {isAnalyzing ? "Cancel" : "New Analysis"}
          </Button>
        )}
      </div>

      {!analysisResult ? (
        <div className="space-y-5">
          {/* Country Selector */}
          <Card className="border border-slate-200 shadow-none rounded-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <Globe size={18} className="text-slate-400" />
              <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Market Region</span>
              <Select value={country} onValueChange={handleCountryChange}>
                <SelectTrigger className="w-[240px] bg-slate-50 border-slate-200 rounded-sm font-mono text-base h-11" data-testid="select-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => (
                    <SelectItem key={c.code} value={c.code} className="text-base">{c.name} ({c.currency})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-400">Used for market pricing and warranty assessment</span>
            </CardContent>
          </Card>

          {/* OBD Integration Card */}
          <Card className={`border shadow-none rounded-sm transition-all ${obdEnabled ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`} data-testid="obd-integration-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${obdEnabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                    <Plug size={20} className={obdEnabled ? 'text-emerald-600' : 'text-slate-400'} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="obd-toggle" className="text-sm font-bold uppercase tracking-wider text-slate-700 cursor-pointer">
                        OBD-II Analysis
                      </Label>
                      {obdEnabled && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Enhanced</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Combine engine sound with real-time ECU diagnostic data for more accurate analysis
                    </p>
                  </div>
                </div>
                <Switch
                  id="obd-toggle"
                  checked={obdEnabled}
                  onCheckedChange={setObdEnabled}
                  disabled={isAnalyzing}
                  data-testid="obd-toggle"
                />
              </div>
              
              {obdEnabled && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <Label htmlFor="obd-scan-id" className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                        OBD Scan ID
                      </Label>
                      <div className="relative">
                        <Input
                          id="obd-scan-id"
                          type="text"
                          placeholder="Enter scan ID from Wisedrive OBD app"
                          value={obdScanId}
                          onChange={(e) => setObdScanId(e.target.value.toUpperCase())}
                          disabled={isAnalyzing}
                          className={`font-mono text-sm h-10 pr-10 ${
                            obdVerifyStatus === 'valid' ? 'border-emerald-400 bg-emerald-50' :
                            obdVerifyStatus === 'invalid' ? 'border-red-400 bg-red-50' : ''
                          }`}
                          data-testid="obd-scan-id-input"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {obdVerifyStatus === 'loading' && <Loader2 size={16} className="text-slate-400 animate-spin" />}
                          {obdVerifyStatus === 'valid' && <CheckCircle size={16} className="text-emerald-500" />}
                          {obdVerifyStatus === 'invalid' && <AlertCircle size={16} className="text-red-500" />}
                        </div>
                      </div>
                    </div>
                    
                    {obdVerifyStatus === 'valid' && obdData && (
                      <div className="flex-shrink-0 bg-white border border-emerald-200 rounded-lg p-3 min-w-[200px]">
                        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">OBD Data Found</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-slate-600">DTCs:</div>
                          <div className="font-semibold text-slate-800">
                            {(obdData.storedDTCs?.length || 0) + (obdData.pendingDTCs?.length || 0) + (obdData.permanentDTCs?.length || 0)}
                          </div>
                          <div className="text-slate-600">Live Data:</div>
                          <div className="font-semibold text-slate-800">{obdData.liveData?.length || 0}</div>
                          <div className="text-slate-600">Protocol:</div>
                          <div className="font-semibold text-slate-800">{obdData.protocol || 'N/A'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded">
                    <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      Get your OBD Scan ID from the <strong>Wisedrive OBD Scanner</strong> app. Connect your OBD-II device, complete a scan, and note the Scan ID shown on the results screen.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <CarDetailsForm
                carData={carData}
                carDetails={carDetails}
                setCarDetails={setCarDetails}
              />
            </div>
            <div className="lg:col-span-1">
              <AudioUploader
                audioFile={audioFile}
                setAudioFile={setAudioFile}
                disabled={isAnalyzing}
              />
            </div>
          </div>

          {!isAnalyzing && (
            <Card className="border border-slate-200 shadow-none rounded-sm">
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-sm text-slate-500 uppercase tracking-wider font-bold">
                    {isFormValid ? "Ready to analyze" : 
                     (obdEnabled && obdVerifyStatus !== 'valid') ? "Enter valid OBD Scan ID" : "Fill all fields to continue"}
                  </div>
                  {obdEnabled && obdVerifyStatus === 'valid' && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                      <Plug size={12} className="mr-1" /> OBD Enhanced
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleAnalyze}
                    disabled={!isFormValid}
                    className="bg-slate-900 text-white rounded-sm hover:bg-slate-800 uppercase tracking-wider font-bold text-sm px-10 py-4"
                    data-testid="analyze-btn"
                  >
                    <Upload size={16} className="mr-2" /> Start Analysis
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {isAnalyzing && (
            <>
              {currentAnalysisId && (
                <div className="bg-slate-100 border border-slate-200 rounded-sm p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Analysis ID:</span>
                    <code className="text-xs font-mono bg-white px-2 py-1 rounded border border-slate-200 text-slate-700 select-all" data-testid="analysis-id">
                      {currentAnalysisId}
                    </code>
                  </div>
                  <button 
                    onClick={() => navigator.clipboard.writeText(currentAnalysisId)}
                    className="text-xs text-slate-500 hover:text-slate-700 underline"
                  >
                    Copy ID
                  </button>
                </div>
              )}
              <ProcessingSteps currentStep={currentStep} />
            </>
          )}
        </div>
      ) : (
        <>
          {analysisResult?.from_cache && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-700" data-testid="cache-indicator">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="m9 12 2 2 4-4"/>
              </svg>
              <span><strong>Instant Result:</strong> This exact configuration was analyzed before. Showing cached result for consistency.</span>
            </div>
          )}
          <ResultsDashboard result={analysisResult} />
        </>
      )}
    </div>
  );
}
